import { useEffect, useState } from "react";
import { version as appVersion } from "../package.json";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import { Hammer, History, Library, Search, Gauge, LineChart, Swords, Coins, Sparkles, Upload, Menu, X, Boxes } from "lucide-react";
import {
  SignedIn,
  SignedOut,
  UserButton,
  useAuth,
  useUser,
} from "@clerk/clerk-react";
import BuildDeck from "./pages/BuildDeck";
import DraftBuilder from "./pages/DraftBuilder";
import UploadDeck from "./pages/UploadDeck";
import Landing from "./pages/Landing";
import TechnicalFaq from "./pages/TechnicalFaq";
import DeckBuildingMethod from "./pages/DeckBuildingMethod";
import DeckList from "./pages/DeckList";
import DeckDetail from "./pages/DeckDetail";
import DeckEdit from "./pages/DeckEdit";
import CardSearch from "./pages/CardSearch";
import MetaSnapshotPage from "./pages/MetaSnapshot";
import Playtest from "./pages/Playtest";
import PlaytestHistory from "./pages/PlaytestHistory";
import PlaytestJob from "./pages/PlaytestJob";
import MegaBuild from "./pages/MegaBuild";
import GauntletJobPage from "./pages/GauntletJob";
import Billing from "./pages/Billing";
import BillingSuccess from "./pages/BillingSuccess";
import Collections from "./pages/Collections";
import BalanceWidget from "./components/BalanceWidget";
import CommandPalette from "./components/CommandPalette";
import PublicShell from "./components/PublicShell";
import { cn } from "./lib/cn";
import { setTokenGetter } from "./lib/auth-token";
import { useLogRocketIdentify } from "./lib/logrocket";
import { useGtagPageViews, useGtagSignUp } from "./lib/gtag";
import { useCheckoutConversions } from "./lib/useCheckoutConversions";

function NavItem({
  to,
  icon: Icon,
  children,
  end,
  inactivePaths,
}: {
  to: string;
  icon: typeof Hammer;
  children: React.ReactNode;
  end?: boolean;
  /** Override NavLink's prefix-match for specific sub-paths. Used to keep
   *  "Saved decks" (`/decks`) from lighting up when "Upload deck"
   *  (`/decks/upload`) is the active route. */
  inactivePaths?: string[];
}) {
  const location = useLocation();
  const suppressActive = inactivePaths?.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
          isActive && !suppressActive
            ? "bg-bg-raised text-fg"
            : "text-fg-muted hover:bg-bg-raised/60 hover:text-fg",
        )
      }
    >
      <Icon size={16} strokeWidth={1.75} />
      {children}
    </NavLink>
  );
}

// Bridges Clerk's useAuth().getToken into the non-React auth-token
// module so api.ts can attach Bearer tokens to fetch + EventSource.
// Mounted once inside <SignedIn>, unmounted on sign-out — keeping the
// getter stale after sign-out isn't a problem because the server would
// 401 the stale JWT anyway.
function ClerkTokenBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    setTokenGetter(() => getToken());
  }, [getToken]);
  return null;
}

// Identifies the signed-in Clerk user to LogRocket once Clerk finishes
// loading. No-op in dev / when VITE_LOGROCKET_APP_ID is unset.
function LogRocketIdentifyBridge() {
  useLogRocketIdentify();
  return null;
}

// Google Ads event layer. Pageviews skip the first mount (the HTML
// gtag config already sent that hit). Sign-up fires only for Clerk
// accounts created in this session. Both no-op outside prod builds.
function GtagBridge() {
  useGtagPageViews();
  useGtagSignUp();
  useCheckoutConversions();
  return null;
}

// Local dev serves two SPAs:
//   :5173 → Vite dev server with HMR (the daily dev URL)
//   :3100 → pre-built bundle served by Bun from /app/web/dist (used to
//           validate the prod build Railway will ship)
// They look identical, but edits to web/src/** only reload on :5173.
// Easy to end up on :3100 by accident and wonder why HMR is broken.
function ProdBundleBanner() {
  const port = typeof window !== "undefined" ? window.location.port : "";
  if (port !== "3100") return null;
  return (
    <div className="sticky top-0 z-50 bg-warn/15 border-b border-warn/40 px-4 py-2 text-xs text-warn flex items-center justify-center gap-2">
      <span>
        You're on the prod-bundle port (<code className="font-mono">:3100</code>).
        For live reload use{" "}
        <a
          href={`http://${window.location.hostname}:5173${window.location.pathname}`}
          className="underline font-medium"
        >
          :5173
        </a>.
      </span>
    </div>
  );
}

export default function App() {
  // Top-level <Routes> so anonymous viewers can reach the public deck
  // page (/decks/:owner/:slug) without hitting the <SignedOut> Landing
  // catch-all. The signed-in vs signed-out branching happens INSIDE
  // each route element — the deck detail page renders for both auth
  // states (PublicShell vs AuthedApp chrome chosen at render time),
  // while every other route falls through to the AuthGatedRouter
  // which preserves the previous Landing-or-AuthedApp behavior.
  //
  // Why not duplicate the deck route under both <SignedIn>/<SignedOut>
  // trees? React Router's tree only matches inside one <Routes>, so
  // duplicating means we'd need two parallel <Routes> blocks (one per
  // auth state) and the route registration would diverge over time.
  // Single top-level <Routes> + auth-aware components inside is the
  // simpler invariant.
  return (
    <>
      <ProdBundleBanner />
      <SignedIn><ClerkTokenBridge /></SignedIn>
      <GtagBridge />
      <Routes>
        <Route
          path="/decks/:owner/:slug"
          element={<PublicAwareDeckDetail />}
        />
        <Route path="/technical-faq" element={<TechnicalFaq />} />
        <Route path="/deck-building-method" element={<DeckBuildingMethod />} />
        <Route path="*" element={<AuthGatedRouter />} />
      </Routes>
    </>
  );
}

// Route element for /decks/:owner/:slug. Anonymous viewers get the
// PublicShell chrome; signed-in viewers (whether owner or someone
// else looking at a public deck) get the full AuthedApp sidebar.
// The DeckDetail page itself renders identically below the chrome —
// it uses useUser() to compute isOwner and pick mode="owner" vs
// mode="public".
function PublicAwareDeckDetail() {
  const { isSignedIn, isLoaded } = useUser();
  // While Clerk loads we render the public shell. If we instead
  // gated the entire page behind isLoaded we'd flash a blank screen
  // for ~50-200ms; rendering anonymous chrome upfront and
  // upgrading to AuthedApp once isSignedIn flips is a smoother
  // experience and the deck content (which hits the public route
  // initially) renders identically either way.
  if (isLoaded && isSignedIn) {
    return (
      <>
        <LogRocketIdentifyBridge />
        <AuthedAppShell>
          <DeckDetail />
        </AuthedAppShell>
      </>
    );
  }
  return (
    <PublicShell>
      <DeckDetail />
    </PublicShell>
  );
}

// Catch-all for everything that isn't the public deck route or the
// technical FAQ. Preserves the prior Landing-vs-AuthedApp branching
// so signed-out users still land on the marketing page when they
// visit /, /decks, /playtest, etc.
function AuthGatedRouter() {
  return (
    <>
      <SignedOut>
        <Routes>
          <Route path="/technical-faq" element={<TechnicalFaq />} />
          <Route path="/deck-building-method" element={<DeckBuildingMethod />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </SignedOut>
      <SignedIn>
        <LogRocketIdentifyBridge />
        <AuthedApp />
      </SignedIn>
    </>
  );
}

function NavSectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">
      {children}
    </div>
  );
}

function NavList() {
  return (
    <>
      <NavSectionHeader>Decks</NavSectionHeader>
      <NavItem to="/" icon={Hammer} end>
        Build deck
      </NavItem>
      <NavItem to="/decks/upload" icon={Upload}>
        Upload deck
      </NavItem>
      <NavItem to="/decks" icon={Library} inactivePaths={["/decks/upload"]}>
        Saved decks
      </NavItem>
      <NavItem to="/draft" icon={Sparkles}>
        Draft builder
      </NavItem>

      <NavSectionHeader>Cards</NavSectionHeader>
      <NavItem to="/search" icon={Search}>
        Card search
      </NavItem>
      <NavItem to="/collections" icon={Boxes}>
        Collections
      </NavItem>

      <NavSectionHeader>Play</NavSectionHeader>
      <NavItem to="/playtest" icon={Swords} end>
        Playtest
      </NavItem>
      <NavItem to="/playtest/history" icon={History}>
        Match history
      </NavItem>
      {/* <NavItem to="/mega-build" icon={Hammer}>
        Mega build
      </NavItem> */}

      <NavSectionHeader>Insights</NavSectionHeader>
      <NavItem to="/meta" icon={LineChart}>
        Meta
      </NavItem>
      <NavItem to="/stats" icon={Gauge}>
        Stats
      </NavItem>

      <NavSectionHeader>Account</NavSectionHeader>
      <NavItem to="/billing" icon={Coins}>
        Credits
      </NavItem>
    </>
  );
}

function SidebarBrand() {
  return (
    <div className="flex items-center gap-2">
      <img src="/logo.svg" alt="" width={24} height={24} aria-hidden="true" />
      <div>
        <div className="text-sm font-semibold tracking-tight">MTG Oracle</div>
        <div className="text-[11px] text-fg-subtle">Beta: Standard Format Only</div>
      </div>
    </div>
  );
}

// Layout chrome used by both AuthedApp (which renders the full inner
// <Routes>) and PublicAwareDeckDetail (which renders just
// <DeckDetail/>). Extracted so signed-in users hitting the public
// deck URL get the same sidebar + palette + balance widget as every
// other authed page, without us re-implementing the chrome twice.
function AuthedAppShell({ children }: { children: React.ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change so tapping a link dismisses it.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Global ⌘K / Ctrl+K to toggle the command palette.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Lock body scroll while drawer is open so the main view doesn't scroll behind.
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  return (
    <div className="flex h-full min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border bg-bg-surface/40">
        <div className="px-4 py-5 border-b border-border">
          <SidebarBrand />
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
          <NavList />
        </nav>
        <BalanceWidget />
        <div className="px-4 py-3 border-t border-border flex items-center justify-between">
          <UserButton afterSignOutUrl="/" />
          <div className="text-[11px] text-fg-subtle">
            {appVersion} · {new Date().getFullYear()}
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 h-14 flex items-center justify-between px-4 border-b border-border bg-bg-base/95 backdrop-blur">
        <button
          type="button"
          aria-label="Open navigation"
          className="btn-ghost p-2 -ml-2"
          onClick={() => setDrawerOpen(true)}
        >
          <Menu size={20} strokeWidth={1.75} />
        </button>
        <SidebarBrand />
        <UserButton afterSignOutUrl="/" />
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <button
            type="button"
            aria-label="Close navigation"
            className="flex-1 bg-black/60"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="w-72 max-w-[85vw] flex flex-col border-l border-border bg-bg-surface shadow-xl">
            <div className="px-4 py-5 border-b border-border flex items-center justify-between">
              <SidebarBrand />
              <button
                type="button"
                aria-label="Close navigation"
                className="btn-ghost p-2 -mr-2"
                onClick={() => setDrawerOpen(false)}
              >
                <X size={20} strokeWidth={1.75} />
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              <NavList />
            </nav>
            <BalanceWidget />
            <div className="px-4 py-3 border-t border-border text-[11px] text-fg-subtle">
              0.1.0 · {new Date().getFullYear()}
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 overflow-x-hidden pt-14 md:pt-0">{children}</main>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

// Full signed-in experience: chrome + the inner route table. Mounted
// from AuthGatedRouter for every URL except /decks/:owner/:slug
// (which has its own top-level route so signed-out users can also
// reach it) and /technical-faq (always-on at the top level).
function AuthedApp() {
  return (
    <AuthedAppShell>
      <Routes>
        <Route path="/" element={<BuildDeck />} />
        <Route path="/decks" element={<DeckList />} />
        <Route path="/decks/upload" element={<UploadDeck />} />
        <Route path="/draft" element={<DraftBuilder />} />
        {/* /decks/:owner/:slug is also mounted at the top level for
            anonymous viewers — keeping the inner registration as
            defense-in-depth so signed-in users still resolve it
            even if the top-level route is ever unmounted. */}
        <Route path="/decks/:owner/:slug" element={<DeckDetail />} />
        <Route path="/decks/:owner/:slug/edit" element={<DeckEdit />} />
        <Route path="/playtest" element={<Playtest />} />
        <Route path="/playtest/history" element={<PlaytestHistory />} />
        <Route path="/playtest/jobs/:id" element={<PlaytestJob />} />
        <Route path="/mega-build" element={<MegaBuild />} />
        <Route path="/mega-build/gauntlet/:id" element={<GauntletJobPage />} />
        <Route path="/meta" element={<MetaSnapshotPage />} />
        <Route path="/search" element={<CardSearch />} />
        <Route path="/stats" element={<StatsPage />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/technical-faq" element={<TechnicalFaq />} />
        <Route path="/deck-building-method" element={<DeckBuildingMethod />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthedAppShell>
  );
}

function NotFound() {
  return (
    <div className="p-8 text-fg-muted">
      Page not found.
    </div>
  );
}

// Lightweight stats page (inline — single-file simple).
import StatsPanel from "./components/StatsPanel";
function StatsPage() {
  return (
    <div className="mx-auto max-w-4xl px-6 md:px-10 py-10">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Database stats</h1>
        <p className="text-sm text-fg-muted mt-1">
          What the agent can see right now. Read-only snapshot.
        </p>
      </header>
      <StatsPanel />
    </div>
  );
}
